import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { confirmPasswordReset, getAuth } from 'firebase/auth'; import {
    Container,
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    Alert,
    CircularProgress
} from '@mui/material';

const ResetPasswordPage: React.FC = () => {
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();

    // Extract the oobCode from URL query parameters
    const queryParams = new URLSearchParams(location.search);
    const oobCode = queryParams.get('oobCode');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate inputs
        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (newPassword.length < 6) {
            setError('Password should be at least 6 characters long');
            return;
        }

        if (!oobCode) {
            setError('Invalid password reset link');
            return;
        }

        setError(null);
        setLoading(true);

        try {
            const auth = getAuth();
            await confirmPasswordReset(auth, oobCode, newPassword);
            setSuccess(true);
            setLoading(false);

            // Redirect to login page after a short delay
            setTimeout(() => {
                navigate('/login');
            }, 3000);
        } catch (err: any) {
            setLoading(false);
            setError(err.message || 'Failed to reset password');
        }
    };

    return (
        <div className="backdrop">
            <Box sx={{ maxWidth: 400, mx: 'auto' }}>
                <Paper elevation={3} sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <img src="/logo.png" alt="Logo" style={{ height: "40px" }} />
                    </Box>
                    <Typography component="h1" variant="h5" gutterBottom>
                        Reset Password
                    </Typography>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {success ? (
                        <Alert severity="success">
                            Password has been reset successfully! Redirecting to login...
                        </Alert>
                    ) : (
                        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                name="newPassword"
                                label="New Password"
                                type="password"
                                id="newPassword"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                autoComplete="new-password"
                            />
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                name="confirmPassword"
                                label="Confirm Password"
                                type="password"
                                id="confirmPassword"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                autoComplete="new-password"
                            />
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                color="primary"
                                sx={{ mt: 3, mb: 2 }}
                                disabled={loading}
                            >
                                {loading ? <CircularProgress size={24} /> : 'Reset Password'}
                            </Button>
                            <Button
                                fullWidth
                                variant="text"
                                onClick={() => navigate('/login')}
                            >
                                Back to Login
                            </Button>
                        </Box>
                    )}
                </Paper>
            </Box>
        </div>
    );
};

export default ResetPasswordPage;